// Weather-Based Task Scheduling Service for HoneyDo Platform
// Automatically adjusts task scheduling based on weather conditions and forecasts
import { apiClient } from '../api/client';
import { 
  MaintenanceSchedule, 
  WeatherCondition, 
  WeatherTrigger,
  SkipCondition 
} from '../types/api';
import { LocationDetails, LocationCoordinates } from './LocationIntelligenceService';

export interface WeatherForecast {
  date: string;
  conditions: WeatherCondition;
  tempHigh: number;
  tempLow: number;
  rainChance: number;
  humidity: number;
  windSpeed: number;
  uvIndex: number;
}

export interface WeatherBasedRecommendation {
  scheduleId: string;
  taskTitle: string;
  currentDate: string;
  recommendedAction: 'proceed' | 'delay' | 'skip' | 'reschedule';
  reason: string;
  alternativeDates?: string[];
  confidence: number; // 0-1 scale
}

export interface TaskOptimizationSuggestion {
  date: string;
  tasks: Array<{
    scheduleId: string;
    title: string;
    category: string;
    weatherSuitability: number; // 0-1 scale
  }>;
  weatherSummary: string;
  optimalTimeOfDay?: 'morning' | 'afternoon' | 'evening';
}

export interface LocationWeatherForecast {
  location: LocationDetails;
  forecast: WeatherForecast[];
  alerts: WeatherAlert[];
  travelTimeImpact?: {
    baseMinutes: number;
    weatherDelayMinutes: number;
    trafficMultiplier: number;
  };
}

export interface WeatherAlert {
  type: 'warning' | 'watch' | 'advisory';
  severity: 'minor' | 'moderate' | 'severe' | 'extreme';
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  affectedActivities: string[];
}

export interface MultiLocationWeatherAnalysis {
  primaryLocation: LocationWeatherForecast;
  alternativeLocations: LocationWeatherForecast[];
  recommendedLocation?: LocationDetails;
  bestWeatherDates: string[];
  weatherWarnings: WeatherAlert[];
}

class WeatherSchedulingService {
  private weatherCache: Map<string, { data: WeatherForecast[]; timestamp: number }> = new Map();
  private cacheExpiryMinutes = 60; // Cache weather data for 1 hour

  /**
   * Get weather forecast for a location
   */
  async getWeatherForecast(
    zipCode?: string, 
    lat?: number, 
    lon?: number, 
    days: number = 7
  ): Promise<WeatherForecast[]> {
    const cacheKey = `${zipCode || `${lat},${lon}`}-${days}`;
    const cached = this.weatherCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheExpiryMinutes * 60 * 1000) {
      return cached.data;
    }

    try {
      const rawForecast = await apiClient.getWeatherForecast({
        zipCode,
        lat,
        lon,
        days
      });

      const forecast = rawForecast.map(day => ({
        date: day.date,
        conditions: this.mapToWeatherCondition(day.conditions),
        tempHigh: day.tempHigh,
        tempLow: day.tempLow,
        rainChance: day.rainChance,
        humidity: 50, // Default values - API should provide these
        windSpeed: 10,
        uvIndex: 6
      }));

      this.weatherCache.set(cacheKey, {
        data: forecast,
        timestamp: Date.now()
      });

      return forecast;
    } catch (error) {
      console.error('Failed to fetch weather forecast:', error);
      return [];
    }
  }

  /**
   * Analyze weather conditions for task scheduling
   */
  async analyzeTaskWeatherSuitability(
    schedule: MaintenanceSchedule,
    targetDate: string
  ): Promise<WeatherBasedRecommendation> {
    const forecast = await this.getWeatherForecast(schedule.zipCode, undefined, undefined, 7);
    const targetForecast = forecast.find(day => day.date === targetDate);
    
    if (!targetForecast) {
      return {
        scheduleId: schedule.scheduleId,
        taskTitle: schedule.title,
        currentDate: targetDate,
        recommendedAction: 'proceed',
        reason: 'Weather data not available',
        confidence: 0.3
      };
    }

    // Check skip conditions
    if (schedule.recurrence.skipConditions) {
      const skipRecommendation = this.evaluateSkipConditions(
        schedule.recurrence.skipConditions,
        targetForecast,
        forecast
      );
      
      if (skipRecommendation) {
        return {
          scheduleId: schedule.scheduleId,
          taskTitle: schedule.title,
          currentDate: targetDate,
          recommendedAction: 'skip',
          reason: skipRecommendation,
          confidence: 0.9
        };
      }
    }

    // Check weather trigger conditions
    if (schedule.recurrence.weatherTrigger) {
      const triggerMatch = this.evaluateWeatherTrigger(
        schedule.recurrence.weatherTrigger,
        targetForecast,
        forecast
      );

      if (!triggerMatch.shouldProceed) {
        const alternativeDates = this.findAlternativeWeatherSuitableDates(
          schedule,
          forecast,
          3
        );

        return {
          scheduleId: schedule.scheduleId,
          taskTitle: schedule.title,
          currentDate: targetDate,
          recommendedAction: alternativeDates.length > 0 ? 'reschedule' : 'delay',
          reason: triggerMatch.reason,
          alternativeDates,
          confidence: 0.85
        };
      }
    }

    // Category-specific weather analysis
    const categoryAnalysis = this.analyzeCategoryWeatherRequirements(
      schedule.category,
      targetForecast,
      schedule.subcategory
    );

    if (!categoryAnalysis.suitable) {
      const alternativeDates = this.findAlternativeWeatherSuitableDates(
        schedule,
        forecast,
        3
      );

      return {
        scheduleId: schedule.scheduleId,
        taskTitle: schedule.title,
        currentDate: targetDate,
        recommendedAction: alternativeDates.length > 0 ? 'reschedule' : 'delay',
        reason: categoryAnalysis.reason,
        alternativeDates,
        confidence: categoryAnalysis.confidence
      };
    }

    return {
      scheduleId: schedule.scheduleId,
      taskTitle: schedule.title,
      currentDate: targetDate,
      recommendedAction: 'proceed',
      reason: 'Weather conditions are suitable',
      confidence: categoryAnalysis.confidence
    };
  }

  /**
   * Get task optimization suggestions for a date range
   */
  async getTaskOptimizationSuggestions(
    schedules: MaintenanceSchedule[],
    zipCode: string,
    startDate: string,
    endDate: string
  ): Promise<TaskOptimizationSuggestion[]> {
    const forecast = await this.getWeatherForecast(zipCode, undefined, undefined, 14);
    const suggestions: TaskOptimizationSuggestion[] = [];

    for (const forecastDay of forecast) {
      if (forecastDay.date >= startDate && forecastDay.date <= endDate) {
        const suitableTasks = [];

        for (const schedule of schedules) {
          if (schedule.weatherDependent) {
            const suitability = this.calculateWeatherSuitability(
              schedule,
              forecastDay
            );

            if (suitability > 0.6) {
              suitableTasks.push({
                scheduleId: schedule.scheduleId,
                title: schedule.title,
                category: schedule.category,
                weatherSuitability: suitability
              });
            }
          }
        }

        if (suitableTasks.length > 0) {
          suggestions.push({
            date: forecastDay.date,
            tasks: suitableTasks.sort((a, b) => b.weatherSuitability - a.weatherSuitability),
            weatherSummary: this.generateWeatherSummary(forecastDay),
            optimalTimeOfDay: this.determineOptimalTimeOfDay(forecastDay)
          });
        }
      }
    }

    return suggestions;
  }

  /**
   * Check if current weather conditions match seasonal triggers
   */
  async checkSeasonalTriggers(
    schedules: MaintenanceSchedule[],
    zipCode: string
  ): Promise<Array<{ schedule: MaintenanceSchedule; triggered: boolean; reason: string }>> {
    const forecast = await this.getWeatherForecast(zipCode, undefined, undefined, 1);
    const today = forecast[0];
    const results = [];

    for (const schedule of schedules) {
      if (schedule.recurrence.seasonalTrigger) {
        const triggered = this.evaluateSeasonalTrigger(
          schedule.recurrence.seasonalTrigger,
          today,
          new Date()
        );
        
        results.push({
          schedule,
          triggered: triggered.triggered,
          reason: triggered.reason
        });
      }
    }

    return results;
  }

  /**
   * Get weather forecasts for multiple locations
   */
  async getMultiLocationWeatherForecast(locations: LocationDetails[], days: number = 7): Promise<LocationWeatherForecast[]> {
    const results: LocationWeatherForecast[] = [];
    
    for (const location of locations) {
      try {
        const forecast = await this.getWeatherForecast(
          undefined,
          location.coordinates.latitude,
          location.coordinates.longitude,
          days
        );

        const alerts = this.extractWeatherAlerts(forecast, location);
        
        const locationForecast: LocationWeatherForecast = {
          location,
          forecast,
          alerts
        };

        results.push(locationForecast);
      } catch (error) {
        console.warn(`Failed to get weather for ${location.name}:`, error);
      }
    }

    return results;
  }

  /**
   * Analyze weather across multiple locations for task planning
   */
  async analyzeMultiLocationWeather(
    taskTitle: string,
    primaryLocation: LocationDetails,
    alternativeLocations: LocationDetails[] = [],
    taskCategory?: string
  ): Promise<MultiLocationWeatherAnalysis> {
    const allLocations = [primaryLocation, ...alternativeLocations];
    const locationForecasts = await this.getMultiLocationWeatherForecast(allLocations);

    if (locationForecasts.length === 0) {
      throw new Error('No weather data available for any location');
    }

    const primaryForecast = locationForecasts[0];
    const alternativeForecast = locationForecasts.slice(1);

    // Find best weather dates across all locations
    const bestWeatherDates = this.findBestWeatherDatesAcrossLocations(
      locationForecasts,
      taskCategory
    );

    // Determine recommended location based on weather
    const recommendedLocation = this.determineRecommendedLocationByWeather(
      locationForecasts,
      taskCategory
    );

    // Collect all weather warnings
    const weatherWarnings = locationForecasts.flatMap(lf => lf.alerts);

    return {
      primaryLocation: primaryForecast,
      alternativeLocations: alternativeForecast,
      recommendedLocation,
      bestWeatherDates,
      weatherWarnings
    };
  }

  /**
   * Get weather-optimized scheduling suggestions for a task across locations
   */
  async getLocationWeatherOptimization(
    taskTitle: string,
    locations: LocationDetails[],
    taskCategory?: string,
    preferredDates?: string[]
  ): Promise<{
    bestLocation: LocationDetails;
    bestDate: string;
    alternatives: Array<{
      location: LocationDetails;
      date: string;
      suitabilityScore: number;
      reason: string;
    }>;
  }> {
    const locationForecasts = await this.getMultiLocationWeatherForecast(locations, 14);
    const alternatives: Array<{
      location: LocationDetails;
      date: string;
      suitabilityScore: number;
      reason: string;
    }> = [];

    let bestLocation = locations[0];
    let bestDate = new Date().toISOString().split('T')[0];
    let bestScore = 0;

    // Evaluate each location-date combination
    for (const locationForecast of locationForecasts) {
      for (const dayForecast of locationForecast.forecast) {
        // Skip if not in preferred dates (if specified)
        if (preferredDates && !preferredDates.includes(dayForecast.date)) {
          continue;
        }

        const suitabilityScore = this.calculateLocationWeatherSuitability(
          locationForecast.location,
          dayForecast,
          taskCategory
        );

        const reason = this.generateWeatherSuitabilityReason(
          dayForecast,
          suitabilityScore,
          taskCategory
        );

        alternatives.push({
          location: locationForecast.location,
          date: dayForecast.date,
          suitabilityScore,
          reason
        });

        if (suitabilityScore > bestScore) {
          bestScore = suitabilityScore;
          bestLocation = locationForecast.location;
          bestDate = dayForecast.date;
        }
      }
    }

    // Sort alternatives by suitability score
    alternatives.sort((a, b) => b.suitabilityScore - a.suitabilityScore);

    return {
      bestLocation,
      bestDate,
      alternatives: alternatives.slice(0, 5) // Top 5 alternatives
    };
  }

  /**
   * Check for weather-related travel delays to locations
   */
  async checkWeatherTravelImpact(
    fromLocation: LocationDetails,
    toLocations: LocationDetails[],
    date: string
  ): Promise<Array<{
    location: LocationDetails;
    baseMinutes: number;
    weatherDelayMinutes: number;
    totalMinutes: number;
    impactLevel: 'none' | 'minor' | 'moderate' | 'severe';
    reason: string;
  }>> {
    const results = [];
    
    // Get weather forecast for the travel date
    const fromForecast = await this.getWeatherForecast(
      undefined,
      fromLocation.coordinates.latitude,
      fromLocation.coordinates.longitude,
      1
    );

    const travelWeather = fromForecast.find(day => day.date === date);
    if (!travelWeather) {
      console.warn('No weather data available for travel date');
      return [];
    }

    for (const toLocation of toLocations) {
      // Calculate base travel time (this would ideally integrate with a mapping service)
      const baseMinutes = this.estimateBaseTravelTime(fromLocation, toLocation);
      
      // Calculate weather-related delays
      const { delayMinutes, impactLevel, reason } = this.calculateWeatherTravelDelay(
        travelWeather,
        baseMinutes
      );

      results.push({
        location: toLocation,
        baseMinutes,
        weatherDelayMinutes: delayMinutes,
        totalMinutes: baseMinutes + delayMinutes,
        impactLevel,
        reason
      });
    }

    return results;
  }

  // Private helper methods for multi-location support

  private extractWeatherAlerts(forecast: WeatherForecast[], location: LocationDetails): WeatherAlert[] {
    const alerts: WeatherAlert[] = [];
    
    for (const day of forecast) {
      // High temperature alert
      if (day.tempHigh > 95) {
        alerts.push({
          type: 'advisory',
          severity: day.tempHigh > 100 ? 'moderate' : 'minor',
          title: 'High Temperature Alert',
          description: `Temperature expected to reach ${day.tempHigh}°F at ${location.name}`,
          startTime: day.date,
          endTime: day.date,
          affectedActivities: ['outdoor work', 'physical labor', 'landscaping']
        });
      }

      // Precipitation alert
      if (day.conditions === 'rainy' && day.rainChance > 70) {
        alerts.push({
          type: 'warning',
          severity: day.rainChance > 90 ? 'moderate' : 'minor',
          title: 'Heavy Rain Expected',
          description: `${day.rainChance}% chance of rain at ${location.name} on ${day.date}`,
          startTime: day.date,
          endTime: day.date,
          affectedActivities: ['outdoor work', 'painting', 'construction', 'travel']
        });
      }

      // Cold weather alert
      if (day.tempLow < 32) {
        alerts.push({
          type: 'advisory',
          severity: day.tempLow < 20 ? 'moderate' : 'minor',
          title: 'Freezing Temperature Alert',
          description: `Temperature expected to drop to ${day.tempLow}°F at ${location.name}`,
          startTime: day.date,
          endTime: day.date,
          affectedActivities: ['plumbing work', 'painting', 'concrete work']
        });
      }
    }
    
    return alerts;
  }

  private findBestWeatherDatesAcrossLocations(
    locationForecasts: LocationWeatherForecast[],
    taskCategory?: string
  ): string[] {
    const dateScores: Map<string, number> = new Map();
    
    for (const locationForecast of locationForecasts) {
      for (const dayForecast of locationForecast.forecast) {
        const score = this.calculateLocationWeatherSuitability(
          locationForecast.location,
          dayForecast,
          taskCategory
        );
        
        const currentScore = dateScores.get(dayForecast.date) || 0;
        dateScores.set(dayForecast.date, Math.max(currentScore, score));
      }
    }

    // Return dates sorted by best weather score
    return Array.from(dateScores.entries())
      .filter(([, score]) => score > 0.6) // Only good weather days
      .sort(([, a], [, b]) => b - a) // Sort by score descending
      .slice(0, 5) // Top 5 dates
      .map(([date]) => date);
  }

  private determineRecommendedLocationByWeather(
    locationForecasts: LocationWeatherForecast[],
    taskCategory?: string
  ): LocationDetails | undefined {
    let bestLocation: LocationDetails | undefined;
    let bestScore = 0;

    for (const locationForecast of locationForecasts) {
      // Calculate average weather suitability for this location
      const scores = locationForecast.forecast.map(day =>
        this.calculateLocationWeatherSuitability(
          locationForecast.location,
          day,
          taskCategory
        )
      );
      
      const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      
      if (avgScore > bestScore) {
        bestScore = avgScore;
        bestLocation = locationForecast.location;
      }
    }

    return bestScore > 0.6 ? bestLocation : undefined;
  }

  private calculateLocationWeatherSuitability(
    location: LocationDetails,
    weather: WeatherForecast,
    taskCategory?: string
  ): number {
    let score = 1.0;

    // Base weather suitability
    score = this.calculateWeatherSuitability({ category: taskCategory || 'general' } as any, weather);

    // Location-specific adjustments
    if (location.type === 'outdoor') {
      // Outdoor locations are more sensitive to weather
      if (weather.conditions === 'rainy') score *= 0.3;
      if (weather.tempHigh > 90) score *= 0.7;
    } else if (location.type === 'store' || location.type === 'service') {
      // Indoor locations are less affected by weather but travel is still impacted
      if (weather.conditions === 'rainy') score *= 0.8; // Some impact from getting there
      if (weather.conditions === 'snow') score *= 0.6;  // More impact for snow
    }

    return Math.max(0, Math.min(1, score));
  }

  private generateWeatherSuitabilityReason(
    weather: WeatherForecast,
    score: number,
    taskCategory?: string
  ): string {
    // Collect weather issues
    const issues = [];
    if (weather.conditions === 'rainy') issues.push('rain expected');
    if (weather.tempHigh > 90) issues.push('very hot');
    if (weather.tempHigh < 50 || weather.tempLow < 40) issues.push('too cold');
    if (weather.windSpeed > 20) issues.push('windy');

    if (score > 0.8) {
      return `Great day for this task: ${weather.conditions}, ${weather.tempLow}°-${weather.tempHigh}°F`;
    } else if (score > 0.6) {
      return issues.length > 0
        ? `Acceptable but ${issues.join(', ')}: ${weather.tempLow}°-${weather.tempHigh}°F`
        : `Good conditions: ${weather.conditions}, ${weather.tempLow}°-${weather.tempHigh}°F`;
    } else if (score > 0.4) {
      return `Fair weather but challenging: ${issues.join(', ')}`;
    } else {
      return `Not recommended: ${issues.length > 0 ? issues.join(', ') : weather.conditions}`;
    }
  }

  private estimateBaseTravelTime(from: LocationDetails, to: LocationDetails): number {
    // Simple distance-based estimation (would be replaced with real routing API)
    const distance = this.calculateHaversineDistance(
      from.coordinates,
      to.coordinates
    );
    
    // Estimate 30 mph average speed including traffic and stops
    return Math.round(distance * 2);
  }

  private calculateHaversineDistance(coord1: LocationCoordinates, coord2: LocationCoordinates): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRadians(coord2.latitude - coord1.latitude);
    const dLon = this.toRadians(coord2.longitude - coord1.longitude);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.toRadians(coord1.latitude)) * Math.cos(this.toRadians(coord2.latitude)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  private calculateWeatherTravelDelay(
    weather: WeatherForecast,
    baseMinutes: number
  ): { delayMinutes: number; impactLevel: 'none' | 'minor' | 'moderate' | 'severe'; reason: string } {
    let delayMinutes = 0;
    let impactLevel: 'none' | 'minor' | 'moderate' | 'severe' = 'none';
    let reason = 'No weather impact expected';

    if (weather.conditions === 'rainy') {
      if (weather.rainChance > 80) {
        delayMinutes = Math.round(baseMinutes * 0.3); // 30% delay for heavy rain
        impactLevel = 'moderate';
        reason = 'Heavy rain may slow traffic';
      } else if (weather.rainChance > 50) {
        delayMinutes = Math.round(baseMinutes * 0.15); // 15% delay for moderate rain
        impactLevel = 'minor';
        reason = 'Light rain may cause minor delays';
      }
    } else if (weather.conditions === 'snow') {
      delayMinutes = Math.round(baseMinutes * 0.5); // 50% delay for snow
      impactLevel = 'severe';
      reason = 'Snow conditions will significantly slow travel';
    } else if (weather.windSpeed > 25) {
      delayMinutes = Math.round(baseMinutes * 0.1); // 10% delay for high winds
      impactLevel = 'minor';
      reason = 'High winds may cause minor delays';
    }

    return { delayMinutes, impactLevel, reason };
  }

  // Private helper methods

  private mapToWeatherCondition(condition: string): WeatherCondition {
    const lowerCondition = condition.toLowerCase();
    if (lowerCondition.includes('rain') || lowerCondition.includes('storm')) return 'rainy';
    if (lowerCondition.includes('sun') || lowerCondition.includes('clear')) return 'sunny';
    if (lowerCondition.includes('snow')) return 'snow';
    if (lowerCondition.includes('wind')) return 'windy';
    if (lowerCondition.includes('humid')) return 'humid';
    return 'sunny'; // Default
  }

  private evaluateSkipConditions(
    skipConditions: SkipCondition[],
    targetWeather: WeatherForecast,
    forecast: WeatherForecast[]
  ): string | null {
    for (const condition of skipConditions) {
      // Check temperature conditions
      if (condition.temperature) {
        if (condition.temperature.min && targetWeather.tempLow < condition.temperature.min) {
          return `Temperature too cold (${targetWeather.tempLow}°F < ${condition.temperature.min}°F)`;
        }
        if (condition.temperature.max && targetWeather.tempHigh > condition.temperature.max) {
          return `Temperature too hot (${targetWeather.tempHigh}°F > ${condition.temperature.max}°F)`;
        }
      }

      // Check weather conditions in forecast
      if (condition.weatherConditions) {
        const forecastDays = condition.forecastDays || 2;
        const upcomingDays = forecast.slice(0, forecastDays);
        
        for (const weather of condition.weatherConditions) {
          if (upcomingDays.some(day => day.conditions === weather)) {
            return `${weather} expected in next ${forecastDays} days`;
          }
        }
      }
    }

    return null;
  }

  private evaluateWeatherTrigger(
    trigger: WeatherTrigger,
    targetWeather: WeatherForecast,
    forecast: WeatherForecast[]
  ): { shouldProceed: boolean; reason: string } {
    const { condition, operator, duration, threshold } = trigger;

    if (operator === 'after' && duration) {
      // Check if we've had the condition for specified duration
      const recentDays = forecast.slice(-duration);
      const matchingDays = recentDays.filter(day => day.conditions === condition).length;
      
      if (matchingDays < duration) {
        return {
          shouldProceed: false,
          reason: `Need ${duration} days of ${condition} weather, only had ${matchingDays}`
        };
      }
    }

    if (threshold && (operator === 'above' || operator === 'below')) {
      const currentValue = condition === 'hot' || condition === 'cold' ? 
        targetWeather.tempHigh : targetWeather.rainChance;
      
      if (operator === 'above' && currentValue <= threshold) {
        return {
          shouldProceed: false,
          reason: `${condition} threshold not met (${currentValue} <= ${threshold})`
        };
      }
      
      if (operator === 'below' && currentValue >= threshold) {
        return {
          shouldProceed: false,
          reason: `${condition} threshold exceeded (${currentValue} >= ${threshold})`
        };
      }
    }

    return { shouldProceed: true, reason: 'Weather trigger conditions met' };
  }

  private findAlternativeWeatherSuitableDates(
    schedule: MaintenanceSchedule,
    forecast: WeatherForecast[],
    maxAlternatives: number
  ): string[] {
    const alternatives: string[] = [];
    
    for (const day of forecast.slice(1)) { // Skip today
      if (alternatives.length >= maxAlternatives) break;
      
      const suitability = this.calculateWeatherSuitability(schedule, day);
      if (suitability > 0.7) {
        alternatives.push(day.date);
      }
    }

    return alternatives;
  }

  private analyzeCategoryWeatherRequirements(
    category: string,
    weather: WeatherForecast,
    subcategory?: string
  ): { suitable: boolean; reason: string; confidence: number } {
    // Category-specific weather requirements
    switch (category) {
      case 'landscaping':
      case 'yard':
        if (weather.conditions === 'rainy' || weather.rainChance > 60) {
          return {
            suitable: false,
            reason: 'Rain expected - not suitable for outdoor work',
            confidence: 0.9
          };
        }
        if (weather.tempHigh > 95) {
          return {
            suitable: false,
            reason: 'Temperature too hot for outdoor work',
            confidence: 0.8
          };
        }
        break;

      case 'exterior':
        if (weather.conditions === 'rainy' || weather.rainChance > 40) {
          return {
            suitable: false,
            reason: 'Rain expected - not suitable for exterior work',
            confidence: 0.85
          };
        }
        break;

      case 'pest-control':
        if (subcategory?.includes('spray') && weather.rainChance > 30) {
          return {
            suitable: false,
            reason: 'Rain may wash away pest control treatment',
            confidence: 0.9
          };
        }
        break;
    }

    return {
      suitable: true,
      reason: 'Weather conditions are suitable',
      confidence: 0.8
    };
  }

  private calculateWeatherSuitability(
    schedule: MaintenanceSchedule,
    weather: WeatherForecast
  ): number {
    let score = 1.0;

    // Temperature penalties
    if (weather.tempHigh > 90) score -= 0.3;
    if (weather.tempLow < 40) score -= 0.2;

    // Rain penalties for outdoor work
    if (['landscaping', 'yard', 'exterior'].includes(schedule.category)) {
      if (weather.conditions === 'rainy') score -= 0.5;
      if (weather.rainChance > 50) score -= 0.3;
      if (weather.rainChance > 25) score -= 0.1;
    }

    // Wind penalties for certain tasks
    if (weather.windSpeed > 20 && ['exterior', 'pest-control'].includes(schedule.category)) {
      score -= 0.2;
    }

    return Math.max(0, Math.min(1, score));
  }

  private generateWeatherSummary(weather: WeatherForecast): string {
    const conditions = [];
    
    conditions.push(`${weather.conditions}`);
    conditions.push(`${weather.tempLow}°-${weather.tempHigh}°F`);
    
    if (weather.rainChance > 20) {
      conditions.push(`${weather.rainChance}% rain chance`);
    }

    return conditions.join(', ');
  }

  private determineOptimalTimeOfDay(weather: WeatherForecast): 'morning' | 'afternoon' | 'evening' {
    if (weather.tempHigh > 85) return 'morning';
    if (weather.uvIndex > 7) return 'evening';
    return 'afternoon';
  }

  private evaluateSeasonalTrigger(
    trigger: any,
    weather: WeatherForecast,
    date: Date
  ): { triggered: boolean; reason: string } {
    // Simplified seasonal logic - would need more sophisticated implementation
    const month = date.getMonth();
    
    switch (trigger.timing) {
      case 'first-warm-day':
        if (weather.tempHigh > 60 && month >= 2 && month <= 4) {
          return { triggered: true, reason: 'First warm day of spring detected' };
        }
        break;
      
      case 'first-frost':
        if (weather.tempLow < 32 && month >= 9) {
          return { triggered: true, reason: 'First frost detected' };
        }
        break;
    }

    return { triggered: false, reason: 'Seasonal conditions not yet met' };
  }
}

// Export singleton instance
export const weatherSchedulingService = new WeatherSchedulingService();

export default weatherSchedulingService;